<template>
  <div class="comments" :class="{ 'comments--all-days': activeSection === 'meals' && showAllDays }">
    <GeneralDietComment
      v-if="activeSection === 'general'"
      :comment="weekComment"
      :draft="tempWeekComment"
      :editing="isEditingWeekComment"
      :editable="canDietBeEdited"
      :empty="weekCommentEmpty"
      @cancel="hideWeekCommentEdit"
      @edit="showWeekCommentEdit"
      @save="saveWeekComment"
      @update:draft="updateTempWeekComment"
    />
    <DietCommentsGrid
      v-if="activeSection === 'meals'"
      :get-day-header="getDayHeader"
      :editable="canDietBeEdited"
      :edited-comment="editedComment"
      :get-comment="getComment"
      :grid-style="commentsGridStyle"
      :items="commentsAndMeals"
      :show-all-days="showAllDays"
      :visible-day-indexes="visibleDayIndexes"
      @content="(item, value) => handleUpdateCommentContent(item.mealIndex, item.dayIndex, item.commentIndex, value)"
      @delete="item => handleDeleteComment(item.mealIndex, item.dayIndex, item.commentIndex)"
      @edit="setEditedComment"
      @title="(item, value) => handleUpdateCommentTitle(item.mealIndex, item.dayIndex, item.commentIndex, value)"
    />
  </div>
</template>

<script lang="ts" setup>
  import { G } from '@mobily/ts-belt'
  import { useEventListener } from '@vueuse/core'
  import { storeToRefs } from 'pinia'
  import { computed, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'

  import { DIET_WEEK_DAYS_COUNT, getScheduleDayHeader } from '@/const'
  import { useDietDetailProvider } from '@/pages/diets/provider'
  import { useDietsStore } from '@/store/dietsStore'
  import { getDietDaysCount, getDietWeeksCount, getWeekDayCount } from '@/utils/dietDays'

  import { buildCommentViewModels } from './commentViewModels'
  import DietCommentsGrid from './components/DietCommentsGrid/DietCommentsGrid.vue'
  import GeneralDietComment from './components/GeneralDietComment/GeneralDietComment.vue'

  const COMMENT_SECTIONS = ['general', 'meals'] as const
  type CommentSection = (typeof COMMENT_SECTIONS)[number]

  const dietStore = useDietsStore()
  const route = useRoute()
  const { updateCommentTitle, updateCommentContent, deleteComment } = dietStore
  const { diet, canDietBeEdited } = storeToRefs(dietStore)
  const { showAllDays } = useDietDetailProvider()
  const editedComment = ref<string | undefined>()
  const tempWeekComment = ref('')
  const isEditingWeekComment = ref(false)

  const activeSection = computed<CommentSection>(() => {
    const section = route.query.section?.toString() ?? 'general'
    return section === 'meals' ? 'meals' : 'general'
  })

  const daysCount = computed(() => getDietDaysCount(diet.value))
  const weekIndexes = computed(() =>
    Array.from({ length: getDietWeeksCount(daysCount.value) }, (_, index) => index + 1),
  )
  const dayIndexes = computed(() => Array.from({ length: daysCount.value }, (_, index) => index))

  const activeWeek = computed(() => {
    const week = parseInt(route.query.week?.toString() ?? '1')

    return weekIndexes.value.includes(week) ? week : 1
  })
  const getWeekStartDayIndex = (week: number) => (week - 1) * DIET_WEEK_DAYS_COUNT
  const visibleDayIndexes = computed(() => {
    if (showAllDays.value) return dayIndexes.value

    const startDayIndex = getWeekStartDayIndex(activeWeek.value)
    const weekDaysCount = getWeekDayCount(daysCount.value, activeWeek.value)
    return Array.from({ length: weekDaysCount }, (_, index) => startDayIndex + index)
  })
  const commentsGridStyle = computed(() => ({
    gridTemplateColumns: `repeat(${visibleDayIndexes.value.length}, minmax(0, 1fr))`,
  }))

  const weekComment = computed({
    get() {
      return diet.value?.data.week_comment ?? ''
    },
    set(value: string) {
      dietStore.updateWeekComment(value.trim())
    },
  })
  const weekCommentEmpty = computed(() => {
    return weekComment.value === '' || weekComment.value === '<p></p>'
  })

  const commentsAndMeals = computed(() => buildCommentViewModels(diet.value, daysCount.value))
  const getDayHeader = (dayIndex: number) =>
    getScheduleDayHeader(dayIndex, diet.value?.data.start_date)

  const hideWeekCommentEdit = () => {
    isEditingWeekComment.value = false
    tempWeekComment.value = ''
  }
  const showWeekCommentEdit = () => {
    tempWeekComment.value = weekComment.value
    hideComment()
    isEditingWeekComment.value = true
  }
  const updateTempWeekComment = (value: string) => {
    tempWeekComment.value = value
  }
  const saveWeekComment = () => {
    weekComment.value = tempWeekComment.value
    hideWeekCommentEdit()
  }

  watch(activeSection, () => {
    hideWeekCommentEdit()
  })

  const setEditedComment = (value: string) => {
    hideComment()
    editedComment.value = value
  }
  const hideComment = (event?: MouseEvent | KeyboardEvent) => {
    if (event?.type === 'keyup') {
      if ('code' in event && event.code === 'Escape') {
        editedComment.value = undefined
      }
    } else {
      editedComment.value = undefined
    }
  }
  const getComment = (mealIndex: number, dayIndex: number, commentIndex: number) => {
    let comment =
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      diet.value?.data.meals[mealIndex]?.days?.[dayIndex]?.comments?.[commentIndex] ?? null
    if (G.isNullable(comment)) {
      comment = {
        content: '',
        title: '',
      }
    }
    return comment
  }
  const handleUpdateCommentTitle = (
    mealIndex: number,
    dayIndex: number,
    commentIndex: number,
    title: string,
  ) => {
    updateCommentTitle({
      commentIndex,
      dayIndex,
      mealIndex,
      title: title.trim(),
    })
  }
  const handleUpdateCommentContent = (
    mealIndex: number,
    dayIndex: number,
    commentIndex: number,
    content: string,
  ) => {
    updateCommentContent({
      commentIndex,
      content: content.trim(),
      dayIndex,
      mealIndex,
    })
  }
  const handleDeleteComment = (mealIndex: number, dayIndex: number, commentIndex: number) => {
    hideComment()
    deleteComment({
      commentIndex,
      dayIndex,
      mealIndex,
    })
  }
  useEventListener(document, 'click', hideComment)
  useEventListener(document, 'keyup', hideComment)
</script>

<style lang="scss">
.comments {
  background: #fff;
  max-width: 100%;
  min-width: 0;
  overflow-x: clip;
  width: 100%;

.comment-week {
  padding: 16px;
}

.comment-week__header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.comment-week__title {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 12px;
}

.comment-week__title-text {
  font-size: 24px;
  font-family: Montserrat-Medium;
  margin-left: 8px;
}

.comment-week__empty {
  border: 1px dashed #f0f0f0;
  padding: 14px;
  font-size: 14px;
  line-height: 18px;
}

.comment-week__empty--editable,
.comment-week__content--editable {
  cursor: pointer;
}

.comment-week__edit {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.comment-week__edit-actions {
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: flex-end;
}

.comment-week__content {
  padding: 14px;
  border: 1px solid #f0f0f0;
}

.comment-week__content-text {
  white-space: pre-line;
  font-size: 14px;
  line-height: 18px;
  overflow-wrap: break-word;
  word-break: break-all;
}

.comments-grid {
  display: grid;
  margin: 6px 0 24px;
}

.comments-grid--header {
  position: sticky;
  top: 58px;
  z-index: 11;
  margin: 0;
  background: #fff;
}

.counter {
  color: var(--color-main-110);
  text-align: right;
  font-size: 11px;
}

.counter.warning {
  color: #f55f6d;
}

.day {
  padding: 0 6px;
  height: 100%;
}

.day:hover {
  box-shadow: 0 4px 5px 3px rgb(222 222 222 / 50%);
}

.day-name {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 46px;
  overflow: hidden;
  font-family: Montserrat-Medium;
  font-size: 12px;
  line-height: 16px;
  text-align: center;
}

.day-name__date {
  color: var(--color-secondary-text);
}

.day:hover .day-name {
  color: var(--color-primary-text);
}

.meal {
  background: #f5f5f5;
  border-radius: 4px;
  padding: 11px 8px;
  display: flex;
  flex-direction: column;
  color: var(--color-tertiary-text);
  height: 68px;
  justify-content: space-between;
  box-sizing: content-box;
}

.meal-name-hour {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.meal-name {
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.meal-hour {
  font-size: 11px;
  font-family: Montserrat-Medium;
  align-self: flex-start;
}

.meal-dishes-and-ingredients {
  font-size: 10px;
  max-height: 39px;
  overflow: hidden;
}

.meal-dish-or-ingredient {
  max-height: 13px;
  line-height: 13px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.add-comment-box {
  display: flex;
  align-items: center;
  height: 48px;
}

.add-comment {
  display: flex;
  height: 48px;
  align-items: center;
}

.comment-box {
  overflow: hidden;
}

.comment-box,
.edit-comment-box {
  display: flex;
  height: 186px;
  align-items: center;
}

.comment {
  padding: 11px 8px;
  height: 68px;
  width: 100%;
  font-size: 10px;
  background: var(--color-main-10);
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  box-sizing: content-box;
}

.comment.with-cursor,
.comment-week__show.with-cursor {
  cursor: pointer;
}

.comment-title-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.comment-title {
  font-size: 14px;
  line-height: 16px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
}

.comment-content {
  max-height: 39px;
  line-height: 13px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
}

.comment-delete {
  display: flex;
  align-items: flex-end;
  min-width: 22px;
}

.comment-delete__button {
  display: none;
  position: relative;
  left: 2px;
  top: 5px;
}

.day:hover .comment-delete__button {
  display: block;
}

.edit-comment-box {
  display: flex;
  height: 164px;
  align-items: center;
}

.edit-comment {
  padding: 11px 8px;
  height: 146px;
  width: 100%;
  font-size: 10px;
  background: var(--color-main-10);
  border-radius: 4px;
}

.edit-comment label {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  height: 12px;
  margin-bottom: 3px;
  text-overflow: ellipsis;
  overflow: hidden;
}

.edit-comment__input {
  width: 100%;
  margin-bottom: 12px;
}

.edit-comment__textarea {
  width: 100%;
  height: 67px !important;
  scrollbar-width: thin;
}

.edit-comment__textarea::-webkit-scrollbar {
  width: 6px;
}

.comment-week__edit .base-textarea {
  max-height: 200px;
  scrollbar-width: thin;
}

.comment-week__empty-button {
  font-weight: bold;
}
}
</style>
